import React, { useEffect, useState } from "react";
import ReportSection from "../ReportSection"
import { getOverviewData, getOverviewDataInternal } from "../../../api/external-reports";
import ReportHeaderSection from "../ReportHeaderSection";
import Loader, { Types } from "../../gdk/Loader";
import NbusLobTable from "./NbusLobTable";
import BoundPremiumByLobChart from "./BoundPremiumByLobChart";
import QuotesNbusByLobChart from "./QuotesNbusByLobChart";
import QuotesNbusConversionChart from "./QuotesNbusConversionChart";
import NbusBySegmentChart from "./NbusBySegmentChart";
import TwoMonthComparisonChart from "./TwoMonthComparisonChart";
import NbusByBranchOrProducerChart from "./NbusByBranchOrProducerChart";
import WpByLobChart from "./WpByLobChart";
import CbusLobTable from "./CbusLobTable";
import NoData from "../NoData";
import PifWpRetentionChart from "./PifWpRetentionChart";
import { overviewNewBusinessFilters } from "../ReportsHelper";

export default function ReportOverviewPage({
    user,
    substituteAgentInfo,
    data,
    setData = () => { },
    dataLastUpdatedDate,
    dataStartDate,
    filterOptions,
    selectedDateRange,
    setSelectedDateRange = () => { },
    appliedFilters,
    setAppliedFilters = () => { }
}) {
    const [loading, setLoading] = useState(!data);

    useEffect(() => {
        if (!data) {
            const initialFilters = {
                startDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1, new Date().getMonth(), new Date().getDate() - 1)),
                endDate: new Date(new Date().setDate(new Date().getDate() - 1)),
            };
            (() => {
                loadData(initialFilters);
                setAppliedFilters(initialFilters);
                setSelectedDateRange('T12')
            })();
        }
    }, []);

    async function loadData(selectedFilters = {}) {
        setLoading(true);
        const handleSuccess = (response) => {
            response?.Status === 3 || response?.Status === 6 ? setData(response.Domain) : setData(null);
            setAppliedFilters(selectedFilters);
            setLoading(false);
        }

        const request = {
            ...selectedFilters,
            AgentIds: selectedFilters?.AgentIds ? selectedFilters.AgentIds.map(agent => agent.split('-')[0]) : null,
        }

        if (substituteAgentInfo?.id) {
            const substituteAgentRequest = {
                substituteAgentId: substituteAgentInfo.id,
                ...request
            };
            getOverviewDataInternal(substituteAgentRequest, response => handleSuccess(response))
        } else {
            getOverviewData(request, response => handleSuccess(response))
        }
    }

    const currentBusinessData = data?.CurrentBusiness ?? null;
    const newBusinessData = data?.NewBusiness ?? null;

    return (<>
        <ReportHeaderSection user={user} substituteUser={substituteAgentInfo} title={"Agency Overview"} />
        {!data ? <ReportSection> {loading ? <Loader type={Types.Inline} /> : <NoData />} </ReportSection> : <>
            {currentBusinessData != null &&
                <ReportSection
                    title={dataLastUpdatedDate ? `Current Book of Business as of ${dataLastUpdatedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}` : 'Current Book of Business'}
                    cards={currentBusinessData.MetricCards}
                >
                    {currentBusinessData.LobChartData == null ? <NoData /> : <>
                        <WpByLobChart data={currentBusinessData.LobChartData} />
                        <CbusLobTable data={currentBusinessData.LobChartData} />
                        <PifWpRetentionChart data={currentBusinessData.MonthChartData} />
                    </>}
                </ReportSection>
            }
            <ReportSection
                title="New Business"
                cards={!loading && newBusinessData.MetricCards}
                dateFilters
                applyFilters={loadData}
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
                selectedDateRange={selectedDateRange}
                setSelectedDateRange={setSelectedDateRange}
                additionalFilters={overviewNewBusinessFilters}
                dataStartDate={dataStartDate}
                dataLastUpdatedDate={dataLastUpdatedDate}
                filterOptions={filterOptions}
            >
                {loading ? <Loader type={Types.Inline} /> : !newBusinessData?.Charts || Object.values(newBusinessData.Charts).every(chart => chart == null)
                    ? <NoData />
                    : <>
                        {newBusinessData.Charts?.LobTable && <NbusLobTable data={newBusinessData.Charts?.LobTable} />}
                        <QuotesNbusByLobChart data={newBusinessData.Charts?.QuotesNbusByLob} />
                        <TwoMonthComparisonChart data={newBusinessData.Charts.TwoMonthComparison} />
                        <BoundPremiumByLobChart data={newBusinessData.Charts?.BoundPremiumByLob} />
                        <QuotesNbusConversionChart data={newBusinessData.Charts?.QuotesNbusConversionByMonth} />
                        <NbusByBranchOrProducerChart branchData={newBusinessData.Charts?.NbusByBranch} producerData={newBusinessData.Charts?.NbusByProducer} />
                        <div className="stacked-charts">
                            <NbusBySegmentChart lob={"ppa"} data={newBusinessData.Charts?.PpaNbusBySegment} />
                            <NbusBySegmentChart lob={"commercial"} data={newBusinessData.Charts?.CommercialNbusBySegment} />
                        </div>
                    </>
                }
            </ReportSection >
        </>}
    </>
    )
}